import React, { useEffect, useState } from 'react';
import { Button, InputNumber, Loader, Message, Modal, useToaster } from 'rsuite';
import { useTranslation } from 'react-i18next';
import {
  fetchSetting,
  updateSetting,
  fillAvailabilitiesForPeriod,
  fetchHorizonSettings
} from '@/services/setting.service';
import { addDays, format } from 'date-fns';
import { FaCalendarDay, FaClock } from 'react-icons/fa';

const BookingWindow = ({ open, handleClose }) => {
  const toaster = useToaster();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);

  // Desk horizon states
  const [deskValue, setDeskValue] = useState<number | undefined>();
  const [deskInitial, setDeskInitial] = useState<number | undefined>();
  const [deskId, setDeskId] = useState<string | undefined>();
  const [showDeskWarning, setShowDeskWarning] = useState(false);

  // Parking horizon states
  const [parkingDays, setParkingDays] = useState<number | undefined>();
  const [parkingHours, setParkingHours] = useState<number | undefined>();
  const [parkingMinutes, setParkingMinutes] = useState<number | undefined>();
  const [parkingInitial, setParkingInitial] = useState<
    | {
        NBDays: number;
        hour: number;
        minute: number;
      }
    | undefined
  >();
  const [parkingId, setParkingId] = useState<string | undefined>();
  const [showParkingWarning, setShowParkingWarning] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const deskRes = await fetchHorizonSettings('Desk');
      setDeskValue(deskRes?.NBDays);
      setDeskInitial(deskRes?.NBDays);
      setDeskId(deskRes?._id);
      // fetch parking horizon
      const parkingRes = await fetchHorizonSettings('parking');
      setParkingDays(parkingRes?.NBDays);
      setParkingHours(parkingRes?.hour);
      setParkingMinutes(parkingRes?.minute);
      setParkingInitial({
        NBDays: parkingRes?.NBDays,
        hour: parkingRes?.hour,
        minute: parkingRes?.minute
      });
      setParkingId(parkingRes?._id);
      setLoading(false);
    };
    fetchData();
  }, []);

  // Desk horizon change
  const handleDeskChange = (val: number | null) => {
    setDeskValue(val ?? undefined);
    if (val !== undefined && deskInitial !== undefined && val < deskInitial) {
      setShowDeskWarning(true);
    } else {
      setShowDeskWarning(false);
    }
  };

  // Parking horizon change
  const handleParkingChange = (type: 'NBDays' | 'hour' | 'minute', val: number | null) => {
    if (type === 'NBDays') setParkingDays(val ?? undefined);
    if (type === 'hour') setParkingHours(val ?? undefined);
    if (type === 'minute') setParkingMinutes(val ?? undefined);

    if (
      parkingInitial &&
      ((type === 'NBDays' && val !== null && val < parkingInitial.NBDays) ||
        (type === 'hour' && val !== null && val < parkingInitial.hour) ||
        (type === 'minute' && val !== null && val < parkingInitial.minute))
    ) {
      setShowParkingWarning(true);
    } else {
      setShowParkingWarning(false);
    }
  };

  const handleSave = async () => {
    if (!deskValue && deskValue !== 0) {
      toaster.push(
        <Message type="error" duration={5000} showIcon>
          {t('Desk booking window is required')}
        </Message>,
        { placement: 'topCenter' }
      );
      return;
    }

    if (parkingDays === undefined || parkingHours === undefined || parkingMinutes === undefined) {
      toaster.push(
        <Message type="error" duration={5000} showIcon>
          {t('Parking booking window is required')}
        </Message>,
        { placement: 'topCenter' }
      );
      return;
    }

    setLoading(true);
    try {
      // Update desk horizon
      await updateSetting(deskId, { NBDays: deskValue });

      if (deskValue !== undefined && deskInitial !== undefined && deskValue > deskInitial) {
        const today = new Date();
        const startDate = addDays(today, deskInitial + 1);
        const endDate = addDays(today, deskValue);
        await fillAvailabilitiesForPeriod(
          format(startDate, 'yyyy-MM-dd'),
          format(endDate, 'yyyy-MM-dd')
        );
      }

      // Update parking horizon
      await updateSetting(parkingId, {
        NBDays: parkingDays,
        hour: parkingHours,
        minute: parkingMinutes
      });

      toaster.push(
        <Message type="success" duration={5000} showIcon>
          {t('Updated successfully')}
        </Message>,
        { placement: 'topCenter' }
      );
      setLoading(false);
      handleClose();
    } catch (e) {
      setLoading(false);
      toaster.push(
        <Message type="error" duration={5000} showIcon>
          {t(e.message || 'An error occurred')}
        </Message>,
        { placement: 'topCenter' }
      );
    }
  };

  return (
    <Modal open={open} onClose={handleClose} size="md">
      <Modal.Header>
        <Modal.Title style={{ fontWeight: 600, fontSize: 20 }}>{t('Booking window')}</Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ padding: 20 }}>
        {/* Desk Horizon */}
        <div
          style={{
            border: '1px solid #e5e5e5',
            borderRadius: 12,
            padding: 16,
            marginBottom: 20,
            background: '#fafafa',
            boxShadow: '0 2px 6px rgba(0,0,0,0.05)'
          }}
        >
          <h5 style={{ marginBottom: 15, fontWeight: 600, color: '#333' }}>
            {t('Desk booking window')}
          </h5>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <label style={{ width: 80, fontWeight: 500 }}>{t('Days')}:</label>
            <InputNumber
              placeholder={t('Days')}
              min={0}
              value={deskValue}
              onChange={val => handleDeskChange(val)}
              style={{ width: 150 }}
            />
          </div>
          {showDeskWarning && (
            <Message
              type="warning"
              showIcon
              style={{
                marginTop: 15,
                borderRadius: 8,
                backgroundColor: '#fff3f3',
                color: '#d32f2f'
              }}
            >
              {t(
                'Reducing the desk booking window will not cancel existing reservations made beyond the new period.'
              )}
            </Message>
          )}
        </div>

        {/* Parking Horizon */}
        {parkingId && (
          <div
            style={{
              border: '1px solid #e5e5e5',
              borderRadius: 12,
              padding: 16,
              background: '#fafafa',
              boxShadow: '0 2px 6px rgba(0,0,0,0.05)'
            }}
          >
            <h5 style={{ marginBottom: 15, fontWeight: 600, color: '#333' }}>
              {t('Parking booking window')}
            </h5>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <label style={{ width: 80, fontWeight: 500 }}>{t('Days')}:</label>
                <InputNumber
                  placeholder={t('Days')}
                  min={0}
                  value={parkingDays}
                  onChange={val => handleParkingChange('NBDays', val)}
                  style={{ width: 120 }}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <label style={{ width: 80, fontWeight: 500 }}>{t('Hours')}:</label>
                <InputNumber
                  placeholder={t('Hours')}
                  min={0}
                  max={23}
                  value={parkingHours}
                  onChange={val => handleParkingChange('hour', val)}
                  style={{ width: 120 }}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <label style={{ width: 80, fontWeight: 500 }}>{t('Minutes')}:</label>
                <InputNumber
                  placeholder={t('Minutes')}
                  min={0}
                  max={59}
                  step={5}
                  value={parkingMinutes}
                  onChange={val => handleParkingChange('minute', val)}
                  style={{ width: 120 }}
                />
              </div>
            </div>
            {showParkingWarning && (
              <Message
                type="warning"
                showIcon
                style={{
                  marginTop: 15,
                  borderRadius: 8,
                  backgroundColor: '#fff3f3',
                  color: '#d32f2f'
                }}
              >
                {t(
                  'Reducing the parking booking window will not cancel existing reservations made beyond the new period.'
                )}
              </Message>
            )}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer style={{ padding: 16 }}>
        {loading ? (
          <Loader />
        ) : (
          <Button
            onClick={handleSave}
            appearance="primary"
            style={{ borderRadius: 8, fontWeight: 600 }}
          >
            {t('Save')}
          </Button>
        )}
        <Button
          onClick={handleClose}
          appearance="subtle"
          style={{ borderRadius: 8, fontWeight: 500 }}
        >
          {t('Cancel')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default BookingWindow;
